import { RULES_CONSTANT } from 'constant/RuleConstant';
import { RuleParameterOption, RuleParameterSchemaItem, RuleParameterValue, RuleParameterValues } from '../types';

function isOption(value: RuleParameterValue): value is RuleParameterOption {
  return Boolean(value && typeof value === 'object' && 'value' in value);
}

function parameterDisplayValue(value: RuleParameterValue) {
  if (isOption(value)) return value.persistentName || value.label;
  return value ?? '';
}

export function serializeParameterValues(
  schema: RuleParameterSchemaItem[],
  values: RuleParameterValues,
  ruleTypeCode?: string,
  attributeOptions: RuleParameterOption[] = []
) {
  const orderedValues = schema.map((field) => values[field.id]);
  let ruleTypeParams = orderedValues.map(parameterDisplayValue).join(',');
  let ruleTypeParamsId = orderedValues
    .map((value) => (isOption(value) ? `${value.persistentName || value.label}=${value.value}` : null))
    .filter(Boolean)
    .join(',');

  if (ruleTypeCode === RULES_CONSTANT.ASSOCIATED_SUBJECT.RULE_KTREE_PARENT_ATTRIBUTE_INFERENCE) {
    const currentNode = values.CURRENT_NODE_ATTRIBUTE;
    const parentNode = values.PARENT_NODE_ATTRIBUTE;
    const sourceAttribute = values.SOURCE_ATTRIBUTE;
    ruleTypeParams = [
      values.CURRENT_LEVEL,
      parameterDisplayValue(currentNode),
      parameterDisplayValue(parentNode),
      parameterDisplayValue(sourceAttribute),
    ].join(',');
    ruleTypeParamsId = [
      `CURRENT_LEVEL=${values.CURRENT_LEVEL ?? ''}`,
      `CURRENT_NODE_ATTRIBUTE=${isOption(currentNode) ? currentNode.value : ''}`,
      `PARENT_NODE_ATTRIBUTE=${isOption(parentNode) ? parentNode.value : ''}`,
      `SOURCE_ATTRIBUTE=${isOption(sourceAttribute) ? sourceAttribute.value : ''}`,
    ].join(',');
  }

  if (ruleTypeCode === RULES_CONSTANT.TEXT.RULE_SELECT_FROM_KTREE_TABLE) {
    const subject = values.SUBJECT;
    const kTree = values.KTREE;
    ruleTypeParams = `${parameterDisplayValue(subject)},${isOption(kTree) ? kTree.value : kTree ?? ''}`;
  }

  if (ruleTypeCode === RULES_CONSTANT.TEXT.RULE_CONCATENATE_ATTRIBUTES) {
    const concatenation = String(values.ATTRIBUTE_CONCATENATION || '');
    const referencedAttributes = new Set(
      concatenation
        .split(',')
        .filter((value) => value.startsWith('{') && value.endsWith('}'))
        .map((value) => value.slice(1, -1))
    );
    ruleTypeParamsId = attributeOptions
      .filter((option) => referencedAttributes.has(option.label))
      .map((option) => `${option.persistentName || option.label}=${option.value}`)
      .join(',');
    ruleTypeParams = JSON.stringify(values);
  }

  return {
    ruleTypeParams: schema.length ? ruleTypeParams : undefined,
    ruleTypeParamsId: ruleTypeParamsId || undefined,
  };
}

export function areRequiredParametersComplete(
  schema: RuleParameterSchemaItem[],
  values: RuleParameterValues,
  ruleTypeCode?: string
) {
  const complete = schema.every((field) => {
    if (!field.required) return true;
    const value = values[field.id];
    if (value === '' || value === null || value === undefined) return false;
    if (isOption(value)) return Boolean(value.value);
    return true;
  });

  if (!complete) return false;
  if (ruleTypeCode !== RULES_CONSTANT.ASSOCIATED_SUBJECT.RULE_KTREE_PARENT_ATTRIBUTE_INFERENCE) return true;
  const level = Number(values.CURRENT_LEVEL);
  return Number.isInteger(level) && level >= 0 && level <= 8;
}
